import { z } from "zod";
import { usernameSchema } from "@/lib/username";
import type { Gender, ProfileType } from "@/lib/types";

export interface PartnerDraft {
  gender: Gender;
  birthDate: string; // yyyy-mm-dd
  bio: string;
}

export interface JoinDraft {
  profileType: ProfileType | null;
  partners: PartnerDraft[];
  seeking: ProfileType[];
  email: string;
  password: string;
  username: string;
  country: string;
  city: string;
  cityId: number | null;
  /** Virtual-only profile: no city (country stays required — it's identity). */
  virtualOnly: boolean;
  /** Combined 18+/Terms/Privacy affirmation — required before submit. */
  consent: boolean;
}

export const EMPTY_DRAFT: JoinDraft = {
  profileType: null,
  partners: [],
  seeking: [],
  email: "",
  password: "",
  username: "",
  country: "",
  city: "",
  cityId: null,
  virtualOnly: false,
  consent: false,
};

/** Default partner drafts for a chosen profile type. */
export function partnersForType(type: ProfileType): PartnerDraft[] {
  const blank = { birthDate: "", bio: "" };
  if (type === "male") return [{ gender: "m", ...blank }];
  if (type === "female") return [{ gender: "f", ...blank }];
  return [
    { gender: "f", ...blank },
    { gender: "m", ...blank },
  ];
}

/** Whole-number age at `now` for a yyyy-mm-dd birth date. */
export function ageFromBirthDate(birthDate: string, now = new Date()): number | null {
  if (!birthDate) return null;
  const d = new Date(birthDate);
  if (Number.isNaN(d.getTime())) return null;
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age;
}

/** yyyy-mm-dd for exactly 18 years ago — the latest permitted birth date. */
export function maxBirthDate(now = new Date()): string {
  const d = new Date(now);
  d.setFullYear(d.getFullYear() - 18);
  return d.toISOString().slice(0, 10);
}

// --- Per-step validation → { field: i18n key } ------------------------------
// Error values are message keys within the `join` namespace, translated at the
// point of display (the username schema lives outside this file, so its issues
// are mapped to keys by code below).

export type FieldErrors = Record<string, string>;

export function validateStep2(draft: JoinDraft): FieldErrors {
  const errors: FieldErrors = {};
  draft.partners.forEach((p, i) => {
    const key = `partner.${i}`;
    if (!p.birthDate) {
      errors[`${key}.birthDate`] = "errors.birthDateRequired";
      return;
    }
    const age = ageFromBirthDate(p.birthDate);
    if (age === null) errors[`${key}.birthDate`] = "errors.birthDateInvalid";
    else if (age < 18) errors[`${key}.birthDate`] = "errors.dobUnder18";
  });
  return errors;
}

const detailsSchema = z.object({
  email: z.string().trim().email("errors.emailInvalid"),
  password: z
    .string()
    .min(8, "errors.passwordMin")
    .max(128, "errors.passwordMax"),
  username: usernameSchema,
  country: z.string().regex(/^[A-Z]{2}$/, "errors.countryRequired"),
});

/** Maps a username validation issue to its `join.errors.*` key. */
function usernameErrorKey(issue: z.ZodIssue): string {
  switch (issue.code) {
    case "too_small":
      return "errors.usernameMin";
    case "too_big":
      return "errors.usernameMax";
    default:
      return "errors.usernameChars";
  }
}

export function validateStep3(draft: JoinDraft): FieldErrors {
  const errors: FieldErrors = {};
  if (draft.seeking.length === 0) errors.seeking = "errors.seekingRequired";
  if (!draft.consent) errors.consent = "errors.consentRequired";
  const parsed = detailsSchema.safeParse(draft);
  if (!parsed.success) {
    for (const issue of parsed.error.issues) {
      const key = String(issue.path[0]);
      if (errors[key]) continue;
      errors[key] = key === "username" ? usernameErrorKey(issue) : issue.message;
    }
  }
  // Non-virtual profiles have no country field of their own: the country is
  // autofilled by picking a city, so surface a missing country on the city
  // field with picking-focused copy.
  if (errors.country && !draft.virtualOnly) {
    delete errors.country;
    errors.city = "errors.cityRequired";
  }
  return errors;
}
